import Navbar from "../components/general/header/Navbar";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Footer } from "../components/general/footer/Footer";
import { ScrollToTop } from "../components/general/ScrollToTop";
import axiosInstance from "../axios/axiosInstance";
import useLogout from "../hooks/useLogOut";
import { useScrollToTop } from "../hooks/useScrollToTop";
import usePathHistory from "../hooks/usePathHistory";
import EmailPopup from "../components/general/EmailPopup";
import { useEffect, useState } from "react";
import { AuthDialog } from "../components/auth/AuthDialog";
import { ChatPopupProvider } from "../components/chat/ChatPopupContext";
import { ChatPopup } from "../components/chat/ChatPopup";
import MobileBottomNav, { MOBILE_NAV_HEIGHT } from "../components/general/MobileBottomNav";
import { Box, useMediaQuery } from "@mui/material";
export const Root = () => {
  const { pathname, state } = useLocation();
  const navigate = useNavigate();
  const isHomePage = pathname === "/";
  const logout = useLogout();
  const isMobile = useMediaQuery("(max-width:899px)");
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");

  useEffect(() => {
    const openAuthDialog = (event) => {
      const mode = event.detail?.mode === "signup" ? "signup" : "login";
      setAuthMode(mode);
      setAuthDialogOpen(true);
    };

    window.addEventListener("directrent:open-auth", openAuthDialog);
    return () => window.removeEventListener("directrent:open-auth", openAuthDialog);
  }, []);

  useEffect(() => {
    if (!state?.authMode) return;

    setAuthMode(state.authMode === "signup" ? "signup" : "login");
    setAuthDialogOpen(true);
    navigate(pathname, { replace: true, state: null });
  }, [navigate, pathname, state]);

  useEffect(() => {
    // Create an axios interceptor to check if user is authenticated or not.
    const isAuthError = (error) => {
      if (error.config?.skipAuthLogout) {
        return false;
      }

      if (!localStorage.getItem("token")) {
        return false;
      }

      const requestUrl = error.config?.url || "";
      const isLoginRequest = [
        "/user/login",
        "/user/signup",
        "/user/auth/google",
        "/user/auth/facebook",
        "/user/generate-reset-token",
        "/user/reset-password",
      ].some((path) => requestUrl.includes(path));

      if (isLoginRequest) {
        return false;
      }

      const status = error.response?.status;
      const message =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "";

      if (status === 401) {
        return true;
      }

      if (status !== 403) {
        return false;
      }

      return /invalid credentials|blocked|deleted|expired|authorization token/i.test(message);
    };

    const interceptorId = axiosInstance.interceptors.response.use(
      function (response) {
        return response;
      },
      function (error) {
        if (isAuthError(error)) {
          logout("The session expired, try login again", false, "error");
        }
        return Promise.reject(error);
      }
    );

    return () => axiosInstance.interceptors.response.eject(interceptorId);
  }, [logout]);

  // use custom hooks 
  useScrollToTop();  // scroll to top when route changes
  usePathHistory(); // use save path history hook

  return (
    <ChatPopupProvider>
      <Navbar />
      {/* Add bottom padding on mobile so content isn't hidden behind the fixed bottom nav */}
      <Box sx={{ pb: { xs: `${MOBILE_NAV_HEIGHT + 8}px`, md: 0 } }}>
        <Outlet />
      </Box>
      {/* Hide footer on mobile — Dubizzle-style: bottom nav replaces it */}
      <Box sx={{ display: { xs: "none", md: "block" } }}>
        <Footer />
      </Box>
      <ScrollToTop />
      <EmailPopup/>
      <AuthDialog
        open={authDialogOpen}
        mode={authMode}
        onClose={() => setAuthDialogOpen(false)}
        onModeChange={setAuthMode}
      />
      <ChatPopup />
      {/* Dubizzle-style mobile bottom navigation */}
      <MobileBottomNav />
    </ChatPopupProvider>
  );
};
